import { useEffect, useMemo, useState } from 'react';
import itemsData from '@/data/items.json';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { Storage, type StorageItemDTO } from '@/network/RestApi';
import { inventory, type InventoryItem } from '@/systems/InventorySystem';
import { ui } from './theme';
import { CloseButton } from './CloseButton';
import { ItemIcon } from './ItemIcon';

interface ItemTpl {
  id: number;
  name: string;
  type: string;
  subtype?: string;
  effect?: string;
}
const TPL: Record<number, ItemTpl> = Object.fromEntries((itemsData.items as ItemTpl[]).map((i) => [i.id, i]));

const STORAGE_CAPACITY = 32;



type Side = 'inventory' | 'storage';
interface Selection {
  side: Side;
  itemId: number;
}

export function StoragePanel() {
  const ch = useCharacterStore((s) => s.current);
  const close = () => useUIStore.getState().set('storage', false);
  const showToast = useUIStore((s) => s.showToast);

  const [bag, setBag] = useState<InventoryItem[]>([]);
  const [storageItems, setStorageItems] = useState<StorageItemDTO[]>([]);
  const [selected, setSelected] = useState<Selection | null>(null);
  const [busy, setBusy] = useState(false);

  const refreshAll = async () => {
    if (!ch) return;
    inventory.setChar(ch.id);
    const [inv, st] = await Promise.all([
      inventory.refresh(),
      Storage.list(ch.id).catch(() => ({ items: [], capacity: STORAGE_CAPACITY })),
    ]);
    setBag(inv.filter((x) => (x.slotType ?? 'inventory') === 'inventory'));
    setStorageItems(st.items ?? []);
  };

  useEffect(() => {
    refreshAll().catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch?.id]);

  const bagSlots = useMemo(() => {
    const map: (InventoryItem | null)[] = Array.from({ length: 64 }, () => null);
    let cursor = 0;
    for (const it of bag) {
      while (cursor < 64 && map[cursor] !== null) cursor++;
      if (cursor >= 64) break;
      map[cursor++] = it;
    }
    return map;
  }, [bag]);

  const storageSlots = useMemo(() => {
    const map: (StorageItemDTO | null)[] = Array.from({ length: STORAGE_CAPACITY }, () => null);
    const pending: StorageItemDTO[] = [];
    for (const it of storageItems) {
      if (it.slotIndex !== null && it.slotIndex !== undefined && it.slotIndex >= 0 && it.slotIndex < STORAGE_CAPACITY && map[it.slotIndex] === null) {
        map[it.slotIndex] = it;
      } else {
        pending.push(it);
      }
    }
    let cursor = 0;
    for (const it of pending) {
      while (cursor < STORAGE_CAPACITY && map[cursor] !== null) cursor++;
      if (cursor >= STORAGE_CAPACITY) break;
      map[cursor++] = it;
    }
    return map;
  }, [storageItems]);

  const deposit = async (itemId: number) => {
    if (!ch || busy) return;
    setBusy(true);
    try {
      await Storage.deposit(ch.id, { itemId });
      await refreshAll();
      setSelected(null);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (itemId: number) => {
    if (!ch || busy) return;
    setBusy(true);
    try {
      await Storage.withdraw(ch.id, { itemId });
      await refreshAll();
      setSelected(null);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const renderCell = (
    side: Side,
    key: number,
    item: { id: number; templateId: number; quantity: number; upgradeLevel: number; bound?: boolean } | null,
  ) => {
    const tpl = item ? TPL[item.templateId] : null;
    const isSel = !!item && selected && selected.side === side && selected.itemId === item.id;
    return (
      <div
        key={`${side}-${key}`}
        title={item ? (tpl ? `${tpl.name}${item.quantity > 1 ? ` ×${item.quantity}` : ''}${item.upgradeLevel ? ` +${item.upgradeLevel}` : ''}` : `Item #${item.templateId}`) : ''}
        onClick={() => item && setSelected({ side, itemId: item.id })}
        onDoubleClick={() => {
          if (!item) return;
          if (side === 'inventory') deposit(item.id);
          else withdraw(item.id);
        }}
        style={{
          width: 36, height: 36, background: '#0b1220',
          border: isSel ? '2px solid #ffeb3b' : '1px solid #324a5e',
          fontSize: 10, color: '#fff', textAlign: 'center', overflow: 'hidden',
          cursor: item ? 'pointer' : 'default', boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {item && (
          <>
            <ItemIcon templateId={item.templateId} size={32} />
            {item.quantity > 1 && (
              <div style={{
                position: 'absolute', right: 2, bottom: 0,
                fontSize: 9, color: '#fff', textShadow: '0 0 2px #000',
              }}>×{item.quantity}</div>
            )}
            {item.upgradeLevel > 0 && (
              <div style={{
                position: 'absolute', left: 2, top: 0,
                fontSize: 9, color: '#ffeb3b', textShadow: '0 0 2px #000',
              }}>+{item.upgradeLevel}</div>
            )}
          </>
        )}
      </div>
    );
  };

  const selectedItem: { id: number; templateId: number; quantity: number; upgradeLevel: number } | null = (() => {
    if (!selected) return null;
    if (selected.side === 'inventory') {
      const it = bag.find((x) => x.id === selected.itemId);
      return it ? { id: it.id, templateId: it.templateId, quantity: it.quantity, upgradeLevel: it.upgradeLevel } : null;
    }
    const it = storageItems.find((x) => x.id === selected.itemId);
    return it ? { id: it.id, templateId: it.templateId, quantity: it.quantity, upgradeLevel: it.upgradeLevel } : null;
  })();
  const selectedTpl = selectedItem ? TPL[selectedItem.templateId] : null;

  return (
    <div style={{
      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      width: 720, ...ui.panel, zIndex: 100,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong>Kho Cá Nhân (Bento)</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={{ display: 'flex', gap: 14 }}>
        {/* Inventory side */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: '#cfd8dc', marginBottom: 6 }}>
            Túi đồ (double-click để gửi vào kho)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 36px)', gap: 4 }}>
            {bagSlots.map((it, i) => renderCell('inventory', i, it))}
          </div>
        </div>

        {/* Storage side */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: '#cfd8dc', marginBottom: 6 }}>
            Kho ({storageItems.length}/{STORAGE_CAPACITY}) - double-click để rút ra
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 36px)', gap: 4 }}>
            {storageSlots.map((it, i) => renderCell('storage', i, it))}
          </div>
        </div>
      </div>

      {selectedItem && (
        <div style={{
          marginTop: 10, padding: 8, fontSize: 12, color: '#cfd8dc',
          background: '#0b1220', border: '1px solid #324a5e', borderRadius: 4,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, background: '#000', border: '1px solid #324a5e',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><ItemIcon templateId={selectedItem.templateId} size={26} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', color: '#fff' }}>
              {selectedTpl ? selectedTpl.name : `Item #${selectedItem.templateId}`}
              {selectedItem.upgradeLevel > 0 && <span style={{ color: '#ffeb3b' }}> +{selectedItem.upgradeLevel}</span>}
              {selectedItem.quantity > 1 && <span style={{ opacity: 0.85 }}> ×{selectedItem.quantity}</span>}
            </div>
            <div style={{ fontSize: 10, opacity: 0.7 }}>
              {selected?.side === 'inventory' ? 'Đang ở túi đồ' : 'Đang ở trong kho'}
            </div>
          </div>
          {selected?.side === 'inventory' ? (
            <button
              style={{ ...ui.primaryBtn, padding: '4px 12px' }}
              onClick={() => deposit(selectedItem.id)}
              disabled={busy}
            >Gửi vào kho →</button>
          ) : (
            <button
              style={{ ...ui.primaryBtn, padding: '4px 12px' }}
              onClick={() => withdraw(selectedItem.id)}
              disabled={busy}
            >← Rút ra</button>
          )}
        </div>
      )}
    </div>
  );
}
